define((require) => {
    "use strict";

    var _                               = require("underscore"),
        Class                           = require("shared/util/Class"),
        Preconditions                   = require("shared/util/Preconditions"),
        AdapterUtil                     = require("util/AdapterUtil"),
        SalesforceCommerceProductCollection     = require("../model/SalesforceCommerceProductCollection"),
        SalesforceCommerceProductModel          = require("../model/SalesforceCommerceProductModel");

    /**
     * @class {SalesforceCommerceAbstractDataAdapter}
     *
     * A <i>SalesforceCommerceAbstractDataAdapter</i>  will translate a OCAPI Product model in a <tt>SalesforceCommerceProductModel</tt>.
     * The full documentation of the SalesforceCommerce model can be acquire on this address.
     * (https://info.salesforcecommerce.com/DOC1/index.jsp
     *
     * @author Pedro Augusto / <a href="http://www.zmags.com">Zmags</a>
     *
     */
    var SalesforceCommerceAbstractDataAdapter = Class.extend("SalesforceCommerceAbstractDataAdapter", {

        /**
         * Filter a product based on his settings.
         * @abstract
         * @param {Object} target already transformed.
         * @param {Object} settings
         */
        // eslint-disable-next-line no-unused-vars
        filter: function(target, settings) {
            Preconditions.abstractMethodError();
        },

        _mapCache: null,

        /**
         * Singleton to avoid creating several instances of the mapping.
         *
         * @return {Object} Returns the cached maps.
         * @private
         */
        _getMapsInstance: function() {
            if (!this._mapCache) {
                this._mapCache = this.getMaps();
            }

            return this._mapCache;
        },

        /**
         * Map a Product from the SalesforceCommerceWebServer in a SalesforceCommerceProductModel in creator.
         * @abstract
         */
        getMaps: function() {
            Preconditions.abstractMethodError();
        },

        /**
         * Transform the products hits into an array of Ids.
         *
         * @param {Object} products SalesforceCommerce product_search endpoint result.
         * @return {String[]} The matching product ids.
         */
        searchHits: function(products) {
            var ids = [];

            _.each(products.hits, (hit) => {
                ids.push(hit.product_id);
            });

            return ids;
        },

        /**
         * Transform a product into SalesforceCommerceProductModel.<p>
         *  @example
         *  <pre>
         *  {
         *       "brand" : "Creative",
         *       "id" : "creative-zen-v",
         *       "image_groups" : [
         *         {
         *           "images" : [ {
         *             "alt" : "Creative ZEN V Digital Media Player, , small",
         *             "link" : "http://localhost/on/salesforcecommerce.static/Sites-SiteGe..",
         *             "title" : "Creative ZEN V Digital Media Player, "
         *           } ],
         *           "view_type" : "small"
         *         } ],
         *       "name" : "Creative ZEN V Digital Media Player",
         *       "page_description" : "The ZEN V player was designed for people ...",
         *       "page_keywords" : "Creative",
         *       "page_title" : "Creative ZEN V Digital Media Player",
         *       "primary_category_id" : "electronics-digital-media-players",
         *       "short_description" : "The ZEN V player was designed for people...",
         *       "type" : {
         *         "item" : true
         *       },
         *       ...
         *       "c_mediaFormat" : [ "0010", "0020", "0030", "0040" ],
         *       "c_memorySize" : "1GB",
         *       "c_tabDescription" : "The ZEN V player was designed for people ..."
         *   }
         * </pre>
         * @param {Object} product from the SalesforceCommerceWebService.
         * @param {SalesforceCommerceSettingsModel} settings The SalesforceCommerce settings.
         * @return {SalesforceCommerceProductModel} The resulting model.
         */
        productToModel: function(product, settings) {
            Preconditions.checkArgument(product, settings);

            var target = AdapterUtil.map(this._getMapsInstance()).transform(product);

            this.filter(target, settings);

            return new SalesforceCommerceProductModel(target);
        },

        /**
         * Transform an array of products into <tt>SalesforceCommerceProductCollection</tt>.
         * @example
         * <pre>
         * {.. , data:[ {
         *       "brand" : "Creative",
         *       "id" : "creative-zen-v",
         *       "image_groups" : [
         *         {
         *           "images" : [ {
         *             "alt" : "Creative ZEN V Digital Media Player, , small",
         *             "link" : "http://localhost/on/salesforcecommerce.static/Sites-SiteGe..",
         *             "title" : "Creative ZEN V Digital Media Player, "
         *           } ],
         *           "view_type" : "small"
         *         } ],
         *       "name" : "Creative ZEN V Digital Media Player",
         *       "page_description" : "The ZEN V player was designed for people ...",
         *       "page_keywords" : "Creative",
         *       "page_title" : "Creative ZEN V Digital Media Player",
         *       "primary_category_id" : "electronics-digital-media-players",
         *       "short_description" : "The ZEN V player was designed for people...",
         *       "type" : {
         *         "item" : true
         *       },
         *       ...
         *       "c_mediaFormat" : [ "0010", "0020", "0030", "0040" ],
         *       "c_memorySize" : "1GB",
         *       "c_tabDescription" : "The ZEN V player was designed for people ..."
         *   }] ...}
         * </pre>
         * @param {Object} products SalesforceCommerceProducts coming from the SalesforceCommerceService.
         * @param {SalesforceCommerceSettingsModel} settings The SalesforceCommerce settings.
         * @return {SalesforceCommerceProductCollection} The resulting product collection.
         */
        productsToCollection: function(products, settings) {
            var self = this,
                collection = new SalesforceCommerceProductCollection();

            _.each(products.data, (product) => {
                collection.push(self.productToModel(product, settings));
            });

            return collection;
        }
    });

    return SalesforceCommerceAbstractDataAdapter;
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/salesforceCommerceWidget/service/SalesforceCommerceAbstractDataAdapter.js